import {
  CLUB_INFOS,
  DIAMOND_PRODUCT_TYPE,
  DISCOUNT_PAY_ONLY_DEPOSIT,
  JewelryTypeMap,
  SPREAD_4_MONTHS_PLAN_ID,
} from '@shared/lib/constants';
import { updateCart, updateDiscountCodes } from '@shared/lib/shopify';
import { DiscountAllocation, ResultWithError } from '@shared/lib/shopify/types';
import { Membership } from '@shared/lib/shopify/types/customer';
import { Cart } from '@shared/lib/shopify/types/shopify-native';
import { assertPresent, capitalize } from '@shared/lib/utils';
import assert from 'assert';
import { getCartOrderType } from './cart-utils';
import { rPercentage } from './formats';

export function shouldApplyMembershipDiscount(
  cart: Cart | undefined,
  membership: Membership | undefined
): boolean {
  if (!cart || !membership) return false;
  return (
    (membership.discountCode &&
      membership.expiresAt &&
      membership.expiresAt > new Date() &&
      !getAllAppliedDiscountCodes(cart).includes(membership.discountCode)) ||
    false
  );
}

export function renderDiscountTitle(discountTitleOrCode: string): string {
  const foundClub = CLUB_INFOS.find((club) => club.discountCode === discountTitleOrCode);
  if (foundClub) {
    return `${capitalize(foundClub.tier)} Club Member ${rPercentage(foundClub.discount)} off`;
  }
  return discountTitleOrCode;
}

export function renderDiscount(discount: DiscountAllocation): string {
  const titleOrCode = discount.title || discount.code;
  if (!titleOrCode) {
    return 'Discount';
  }
  return renderDiscountTitle(titleOrCode);
}

export function getAllAppliedDiscountCodes(cart: Cart): string[] {
  return Array.from(
    new Set([
      ...cart.discountAllocations.map((allocation) => allocation.code),
      ...cart.lines.flatMap((line) =>
        line.discountAllocations.map((allocation) => allocation.code)
      ),
      ...cart.paymentPlanDiscountAllocations.map((allocation) => allocation.code),
    ])
  ).filter(Boolean) as string[];
}

export function getAppliedDiscountCodes(cart: Cart): string[] {
  return cart.discountAllocations.map((allocation) => allocation.code).filter(Boolean) as string[];
}

export function getCurrentMembershipDiscountCode(cart: Cart): string | undefined {
  return cart.lines
    .flatMap((line) => line.discountAllocations)
    .find((allocation) => allocation.code?.includes('MEMBER'))?.code;
}

export async function maybeApplyMembershipDiscount(
  cart: Cart | undefined,
  membership: Membership | undefined
): Promise<ResultWithError<Cart | undefined>> {
  if (!cart || !membership) return { result: cart };
  if (membership.expiresAt && membership.expiresAt > new Date()) {
    const currentMembershipDiscountCode = getCurrentMembershipDiscountCode(cart);
    const shouldMembershipDiscountCode = assertPresent(
      CLUB_INFOS.find((club) => club.tier === membership.tier)
    ).discountCode;
    if (currentMembershipDiscountCode !== shouldMembershipDiscountCode) {
      const existingDiscountCodes = Array.from(
        new Set(cart.discountAllocations.map((allocation) => allocation.code).filter(Boolean))
      ) as string[];
      let newDiscountCodes = Array.from(existingDiscountCodes);
      if (existingDiscountCodes.some((code) => code.startsWith(DISCOUNT_PAY_ONLY_DEPOSIT))) {
        newDiscountCodes = newDiscountCodes.filter(
          (code) => !code.startsWith(DISCOUNT_PAY_ONLY_DEPOSIT)
        );
        newDiscountCodes.push(`${DISCOUNT_PAY_ONLY_DEPOSIT}_${membership.tier.toUpperCase()}`);
      }
      newDiscountCodes = [
        ...existingDiscountCodes.filter((code) => code !== currentMembershipDiscountCode),
        shouldMembershipDiscountCode,
      ];
      return await updateDiscountCodes(cart, newDiscountCodes);
    }
  }
  return { result: cart };
}

async function updateSellingPlan(cart: Cart, sellingPlanId: string | null): Promise<Cart> {
  const cartLines = cart.lines.filter(
    (line) =>
      [DIAMOND_PRODUCT_TYPE, ...Object.values(JewelryTypeMap)].includes(
        line.merchandise.product.productType
      ) && (line.sellingPlanAllocation?.sellingPlan.id || null) !== sellingPlanId
  );
  if (cartLines.length === 0) {
    return cart;
  }
  return await updateCart(
    cart.id,
    cartLines.map((line) => ({
      id: line.id,
      sellingPlanId: sellingPlanId,
    }))
  );
}

async function addDepositDiscountCode(cart: Cart): Promise<ResultWithError<Cart>> {
  const existingDiscountCodes = getAllAppliedDiscountCodes(cart);
  const currentDepositDiscountCode = existingDiscountCodes.find((code) =>
    code.startsWith(DISCOUNT_PAY_ONLY_DEPOSIT)
  );
  const currentMembershipDiscountCode = getCurrentMembershipDiscountCode(cart);
  const shouldDepositDiscountCode = [
    DISCOUNT_PAY_ONLY_DEPOSIT,
    currentMembershipDiscountCode?.replace('_MEMBER_ORDER', ''),
  ]
    .filter(Boolean)
    .join('_');
  if (currentDepositDiscountCode !== shouldDepositDiscountCode) {
    return await updateDiscountCodes(cart, [
      ...existingDiscountCodes.filter((code) => !code.startsWith(DISCOUNT_PAY_ONLY_DEPOSIT)),
      shouldDepositDiscountCode,
    ]);
  }
  return { result: cart };
}

async function removeDepositDiscountCode(cart: Cart): Promise<ResultWithError<Cart>> {
  const existingDiscountCodes = Array.from(
    new Set(cart.discountAllocations.map((allocation) => allocation.code).filter(Boolean))
  ) as string[];
  if (existingDiscountCodes.some((code) => code.startsWith(DISCOUNT_PAY_ONLY_DEPOSIT))) {
    return await updateDiscountCodes(
      cart,
      existingDiscountCodes.filter((code) => !code.startsWith(DISCOUNT_PAY_ONLY_DEPOSIT))
    );
  }
  return { result: cart };
}

export async function applyDepositDiscountCode(cart: Cart): Promise<ResultWithError<Cart>> {
  try {
    assert(getCartOrderType(cart) === 'confirmation', 'Cart is not a confirmation cart');
    // Maybe remove the pay in 4 months selling plan.
    const updatedCart = await updateSellingPlan(cart, null);
    // Maybe add a half deposit discount code.
    return await addDepositDiscountCode(updatedCart);
  } catch (error) {
    console.error(error);
    return { error: 'Error applying deposit discount code' };
  }
}

export async function applySpread4MonthsPlan(cart: Cart): Promise<ResultWithError<Cart>> {
  try {
    assert(getCartOrderType(cart) === 'confirmation', 'Cart is not a confirmation cart');
    const updatedCart = await updateSellingPlan(cart, SPREAD_4_MONTHS_PLAN_ID);
    return await removeDepositDiscountCode(updatedCart);
  } catch (error) {
    console.error(error);
    return { error: 'Error applying spread 4 months plan' };
  }
}
